// @ts-check

import {
  fetchEnvelope,
  fetchEnvelopeDocuments,
  fetchEnvelopeFields,
  fetchEnvelopeParties,
  fetchEnvelopeSharing,
  patchEnvelope,
  patchEnvelopeFields,
  postEnvelope,
  postEnvelopeDocuments,
  postEnvelopeSharing,
  putEnvelopeParties,
  sendEnvelope,
} from '@/data/api/envelopes';

/**
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeUpdate} TEnvelopeUpdate
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeProgressUpdate} TEnvelopeProgressUpdate
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TCreatingEnvelope} TCreatingEnvelope
 * @typedef {import('@/store/modules/users/models/Sharing').TSharing} TSharing
 * @typedef {import('@/store/modules/users/models/Sharing').TSharingUpdate} TSharingUpdate
 * @typedef {import('@/queries/envelopes/queryKeys').TEnvelopeKeys} TEnvelopeKeys
 * @typedef {import('@/data/schema/parties').TNewOrSavedParties} TUpdateParties
 * @typedef {import('@/data/schema/parties').TPartiesUpdate} TPartiesUpdate
 * @typedef {import('@/store/modules/envelopes/models/EnvelopeFields').TEnvelopeFieldsUpdate} TEnvelopeFieldsUpdate
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeSend} TEnvelopeSend
 */

/**
 * @template {import('@tanstack/vue-query').QueryKey} T
 * @typedef {import('@tanstack/vue-query').QueryFunctionContext<T>} QueryFunctionContext<T>
 */

/**
 * Query function for retrieving envelope details.
 *
 * @param {QueryFunctionContext<ReturnType<TEnvelopeKeys['details']>>} context
 */
export function queryEnvelope({ queryKey, signal }) {
  const [, fingerprint] = queryKey;
  return fetchEnvelope(fingerprint, signal);
}

/**
 * Query function for retrieving envelope documents.
 *
 * @param {QueryFunctionContext<ReturnType<TEnvelopeKeys['documents']>>} context
 */
export function queryEnvelopeDocuments({ queryKey, signal }) {
  const [, fingerprint] = queryKey;
  return fetchEnvelopeDocuments(fingerprint, signal);
}

/**
 * Query function for retrieving envelope parties.
 *
 * @param {QueryFunctionContext<ReturnType<TEnvelopeKeys['parties']>>} context
 */
export function queryEnvelopeParties({ queryKey, signal }) {
  const [, fingerprint] = queryKey;
  return fetchEnvelopeParties(fingerprint, signal);
}

/**
 * Query function for retrieving envelope fields.
 *
 * @param {QueryFunctionContext<ReturnType<TEnvelopeKeys['fields']>>} context
 */
export function queryEnvelopeFields({ queryKey, signal }) {
  const [, fingerprint] = queryKey;
  return fetchEnvelopeFields(fingerprint, signal);
}

/**
 * Query function for retrieving envelope sharing information.
 *
 * @param {QueryFunctionContext<ReturnType<TEnvelopeKeys['sharing']>>} context
 */
export function queryEnvelopeSharing({ queryKey, signal }) {
  const [, fingerprint] = queryKey;
  return fetchEnvelopeSharing(fingerprint, signal);
}

/** @param {string} [fingerprint] */
export function mutateEnvelope(fingerprint) {
  const batchSize = 20;

  /** @param {Partial<TEnvelopeUpdate>} baseData */
  return async (baseData) => {
    // If fingerprint is provided, we are updating an existing envelope and should use PATCH
    const postOrPatch = fingerprint
      ? /** @param {Partial<TEnvelopeUpdate>} data */ async data =>
          patchEnvelope(fingerprint, data)
      : postEnvelope;

    // Split documents into uploads and non-uploads
    const uploads = baseData.documents?.filter(doc => 'file' in doc) || [];
    const nonUploads =
      baseData.documents?.filter(doc => !('file' in doc)) || [];

    // Split uploads into chunks
    const uploadsChunks = Array.from(
      { length: Math.ceil(uploads.length / batchSize) },
      (_, i) => uploads.slice(i * batchSize, (i + 1) * batchSize),
    );

    // Request to create/update envelope with non-upload documents and first chunk of uploads
    const data = {
      ...baseData,
      documents: [...nonUploads, ...(uploadsChunks.shift() || [])],
    };
    const envelope = await postOrPatch(data);

    // Requests to upload the rest of the documents
    await Promise.all(uploadsChunks.map(chunk =>
        postEnvelopeDocuments(envelope.fingerprint, { documents: chunk })));

    return envelope;
  };
}

/** @param {string} fingerprint */
export function mutateEnvelopeProgress(fingerprint) {
  /** @param {TEnvelopeProgressUpdate} data */
  return data => patchEnvelope(fingerprint, data);
}

/** @param {string} fingerprint */
export function mutateEnvelopeWithParties(fingerprint) {
  /** @param {TPartiesUpdate} data */
  return data =>
    Promise.all([
      patchEnvelope(fingerprint, { parallel_signing: data.parallel_signing }),
      putEnvelopeParties(
        fingerprint,
        {
          parties: data.parties,
          envelope_progressed_at: data.envelope_progressed_at,
        },
      ),
    ]);
}

/** @param {string} fingerprint */
export function mutateEnvelopeSharing(fingerprint) {
  /** @param {TSharingUpdate} data */
  return data => postEnvelopeSharing(fingerprint, data);
}

/** @param {string} fingerprint */
export function mutateEnvelopeSend(fingerprint) {
  /** @param {TEnvelopeSend} data */
  return data => sendEnvelope(fingerprint, data);
}

/** @param {string} fingerprint */
export function mutateEnvelopeFields(fingerprint) {
  /** @param {TEnvelopeFieldsUpdate} data */
  return data => patchEnvelopeFields(fingerprint, data);
}
